// src/hooks/notes/useCalloutNotes.js
//
// Proje-notu (callout) sistemi — PAYLAŞILAN hook. Hem JbzViewer (Feed/Projects
// pickMode) hem editör-önizleme (DrawingCanvas preview modu) kullanır → tek kaynak.
//
// Sorumluluk: notları API'den çek (+5s poll), THREE callout'larını sahneye çiz,
// ekle/düzenle/boyutlandır/sil akışı + textarea overlay + paydaş filtresi + "not
// ekle" butonu (controls JSX).
//
// Kamera-agnostik: koordinatlar raycaster ile (screenToWorld) hesaplanır → hem
// frustum-pan (JbzViewer) hem OrbitControls (editör) ile doğru. Etkileşim
// capture-phase native listener + stopPropagation ile host'un pan/selection'ıyla
// çakışmadan yönetilir.

import { useState, useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import API_URL from '../../config';
import {
  CALLOUT_COLOR, CALLOUT_CSS,
  buildCalloutGroup, makeTextSprite, screenToWorld,
  CALLOUT_MIN_FRAME_WIDTH, CALLOUT_MAX_FRAME_WIDTH,
} from '../../utils/notes/calloutRender';

export default function useCalloutNotes({
  getThree,       // () => ({ scene, camera, renderer }) | null
  ready,          // boolean — three hazır mı
  containerRef,   // ref — event + textarea konumu için
  projectId,
  activeTab,
  baseHeight,     // number — callout boyut referansı (scene bbox height)
  enabled = true, // false → hook inert (normal editörde etkileşim bağlanmaz)
}) {
  const [noteMode, setNoteMode] = useState(false);
  const [notes, setNotes] = useState([]);
  const [hiddenAuthors, setHiddenAuthors] = useState(new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const [noteStep, setNoteStep] = useState(0); // 0:idle 1:placed-target 2:typing
  const [textareaPos, setTextareaPos] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const pendingRef = useRef(null);       // { tx,ty, bx,by }
  const textareaRef = useRef(null);
  const calloutGroupsRef = useRef(new Map());
  const previewRef = useRef({ line: null, circle: null });
  const resizeRef = useRef(null);
  const noteIdCounter = useRef(0);

  // Ref-mirror'lar: capture-phase native handler'lar güncel değeri görsün diye
  // (listener'ları her state değişiminde yeniden bağlamamak için).
  const noteModeRef = useRef(noteMode);
  const noteStepRef = useRef(noteStep);
  const notesRef = useRef(notes);
  const baseHRef = useRef(baseHeight);
  useEffect(() => { noteModeRef.current = noteMode; }, [noteMode]);
  useEffect(() => { noteStepRef.current = noteStep; }, [noteStep]);
  useEffect(() => { notesRef.current = notes; }, [notes]);
  useEffect(() => { baseHRef.current = baseHeight; }, [baseHeight]);

  const viewH = () => baseHRef.current || 300;

  // ── API ──
  const apiHeaders = useCallback(() => {
    const token = localStorage.getItem('projabze_token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }, []);

  // Notları çek + 5s poll
  useEffect(() => {
    if (!enabled || !projectId || !activeTab) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/projects/${projectId}/notes`, { headers: apiHeaders() });
        if (!res.ok) return;
        const { notes: serverNotes } = await res.json();
        if (cancelled) return;
        const mapped = (serverNotes || [])
          .filter(n => (n.tabId || null) === activeTab)
          .map(n => ({
            id: n.id,
            text: n.rawText || '',
            tx: n.targetPoint?.x ?? 0,
            ty: n.targetPoint?.y ?? 0,
            bx: n.boxPosition?.x ?? 0,
            by: n.boxPosition?.y ?? 0,
            frameWidth: typeof n.frameWidth === 'number' ? n.frameWidth : (typeof n.scale === 'number' ? n.scale : 1),
            authorName: n.authorName,
            authorRole: n.authorRole,
            authorAvatar: n.authorAvatar,
            createdAt: n.createdAt,
            updatedAt: n.updatedAt,
            _server: true,
          }));
        setNotes(prev => {
          const localOnly = (prev || []).filter(n => !n._server);
          return [...mapped, ...localOnly];
        });
      } catch (_) { /* offline — local notları koru */ }
    };
    load();
    const interval = setInterval(load, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [enabled, projectId, activeTab, apiHeaders]);

  // ── Callout gruplarını sahneyle senkronla (paydaş filtresine göre) ──
  useEffect(() => {
    const three = getThree?.();
    if (!ready || !three) return;
    const { scene } = three;
    const vh = baseHeight || (three.camera.top - three.camera.bottom) || 300;

    const sorted = [...notes].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (ta !== tb) return ta - tb;
      return (a.id || '').localeCompare(b.id || '');
    });
    const authorCounts = {};
    const indexedNotes = sorted.map(n => {
      const key = `${n.authorName || ''}|${n.authorRole || ''}`;
      authorCounts[key] = (authorCounts[key] || 0) + 1;
      return { ...n, _authorIndex: authorCounts[key] };
    });
    const visibleNotes = indexedNotes.filter(n => {
      const key = `${n.authorName || 'Anonim'}|${n.authorRole || ''}`;
      return !hiddenAuthors.has(key);
    });
    const visibleIds = new Set(visibleNotes.map(n => n.id));

    for (const [id, g] of calloutGroupsRef.current) {
      if (!visibleIds.has(id)) { scene.remove(g); calloutGroupsRef.current.delete(id); }
    }
    for (const note of visibleNotes) {
      const existing = calloutGroupsRef.current.get(note.id);
      if (existing) { scene.remove(existing); calloutGroupsRef.current.delete(note.id); }
      const g = buildCalloutGroup(note, vh);
      scene.add(g);
      calloutGroupsRef.current.set(note.id, g);
    }
  }, [notes, ready, baseHeight, hiddenAuthors, getThree]);

  // ── Yerleştirme önizleme (hedef daire + kesikli çizgi) ──
  useEffect(() => {
    const three = getThree?.();
    if (!ready || !three) return;
    const { scene } = three;
    const clean = () => {
      for (const key of ['line', 'circle']) {
        const o = previewRef.current[key];
        if (o) { scene.remove(o); o.geometry?.dispose?.(); o.material?.dispose?.(); previewRef.current[key] = null; }
      }
    };
    clean();
    if (noteStep === 1 && pendingRef.current) {
      const { tx, ty } = pendingRef.current;
      const vh = viewH();
      const cg = new THREE.CircleGeometry(2, 16);
      const cm = new THREE.MeshBasicMaterial({ color: CALLOUT_COLOR, depthTest: false, transparent: true, opacity: 0.8 });
      const circle = new THREE.Mesh(cg, cm);
      circle.position.set(tx, ty, 2); circle.renderOrder = 1000;
      scene.add(circle); previewRef.current.circle = circle;

      const positions = new Float32Array([tx, ty, 2, tx, ty, 2]);
      const lg = new THREE.BufferGeometry();
      lg.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      const lm = new THREE.LineDashedMaterial({
        color: CALLOUT_COLOR, depthTest: false, transparent: true, opacity: 0.6,
        dashSize: vh * 0.006, gapSize: vh * 0.004,
      });
      const line = new THREE.Line(lg, lm); line.computeLineDistances(); line.renderOrder = 1000;
      scene.add(line); previewRef.current.line = line;
    }
    return clean;
  }, [noteStep, ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resize handle hit-test ──
  const hitTestHandle = useCallback((worldPt) => {
    const frac = 0.12;
    for (const [noteId, group] of calloutGroupsRef.current) {
      const sprite = group.children.find(c => c.isSprite);
      if (!sprite) continue;
      const { x: px, y: py } = sprite.position;
      const w = sprite.scale.x, h = sprite.scale.y;
      const brx = px + w, bry = py - h;
      if (worldPt.x >= brx - w * frac && worldPt.x <= brx && worldPt.y >= bry && worldPt.y <= bry + h * frac) {
        return { noteId };
      }
    }
    return null;
  }, []);

  // ── Submit / Delete ──
  const handleNoteSubmit = useCallback(() => {
    const text = textareaRef.current?.value?.trim();
    if (!text || !pendingRef.current) {
      setNoteStep(0); setTextareaPos(null); setEditingId(null); pendingRef.current = null; return;
    }
    const p = pendingRef.current;
    if (editingId) {
      setNotes(prev => prev.map(n => n.id === editingId ? { ...n, text, tx: p.tx, ty: p.ty, bx: p.bx, by: p.by } : n));
      if (projectId) {
        fetch(`${API_URL}/api/projects/${projectId}/notes/${editingId}`, {
          method: 'PATCH', headers: apiHeaders(),
          body: JSON.stringify({ calloutData: { rawText: text, targetPoint: { x: p.tx, y: p.ty }, boxPosition: { x: p.bx, y: p.by }, tabId: activeTab } }),
        }).catch(() => {});
      }
    } else {
      const tempId = 'note_' + (++noteIdCounter.current) + '_' + Date.now();
      setNotes(prev => [...prev, { id: tempId, text, tx: p.tx, ty: p.ty, bx: p.bx, by: p.by }]);
      if (projectId) {
        fetch(`${API_URL}/api/projects/${projectId}/notes`, {
          method: 'POST', headers: apiHeaders(),
          body: JSON.stringify({ calloutData: { rawText: text, targetPoint: { x: p.tx, y: p.ty }, boxPosition: { x: p.bx, y: p.by }, tabId: activeTab, calloutId: tempId } }),
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => { if (data?.note?.id) setNotes(prev => prev.map(n => n.id === tempId ? { ...n, id: data.note.id, _server: true } : n)); })
          .catch(() => {});
      }
    }
    setNoteStep(0); setTextareaPos(null); setEditingId(null); pendingRef.current = null;
  }, [editingId, projectId, activeTab, apiHeaders]);

  const handleNoteDelete = useCallback((id) => {
    setNotes(prev => prev.filter(n => n.id !== id));
    if (projectId) {
      fetch(`${API_URL}/api/projects/${projectId}/notes/${id}`, { method: 'DELETE', headers: apiHeaders() }).catch(() => {});
    }
  }, [projectId, apiHeaders]);

  const handleNoteTextKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleNoteSubmit(); }
    if (e.key === 'Escape') { setNoteStep(0); setTextareaPos(null); setEditingId(null); pendingRef.current = null; }
  }, [handleNoteSubmit]);

  // ── Etkileşim (capture-phase native listener'lar) ──
  useEffect(() => {
    if (!enabled || !ready) return;
    const container = containerRef.current;
    if (!container) return;

    const worldOf = (e) => {
      const t = getThree?.(); if (!t) return null;
      return { pt: screenToWorld(e, t.camera, t.renderer.domElement), three: t };
    };

    const onDown = (e) => {
      if (e.button !== 0) return;
      const w = worldOf(e); if (!w) return;
      // Resize handle (her modda)
      const hit = hitTestHandle(w.pt);
      if (hit) {
        e.stopPropagation(); e.preventDefault();
        const grp = calloutGroupsRef.current.get(hit.noteId);
        const spr = grp?.children.find(c => c.isSprite);
        const startFW = spr?.userData?._frameWidth || 1;
        resizeRef.current = {
          noteId: hit.noteId, startWorldX: w.pt.x, startFrameWidth: startFW, lastFrameWidth: startFW,
          baseWorldW: spr ? spr.scale.x / startFW : 1, text: spr?.userData?._text, header: spr?.userData?._header,
        };
        return;
      }
      // Not modunda host pan/selection'ı engelle (yerleştirme click ile)
      if (noteModeRef.current && noteStepRef.current < 2) { e.stopPropagation(); }
    };

    const onClick = (e) => {
      if (!noteModeRef.current) return;
      const w = worldOf(e); if (!w) return;
      e.stopPropagation();
      const { renderer } = w.three;
      if (noteStepRef.current === 0) {
        const off = viewH() * 0.08;
        pendingRef.current = { tx: w.pt.x, ty: w.pt.y, bx: w.pt.x + off, by: w.pt.y + off };
        setNoteStep(1);
      } else if (noteStepRef.current === 1) {
        pendingRef.current.bx = w.pt.x; pendingRef.current.by = w.pt.y;
        const rect = renderer.domElement.getBoundingClientRect();
        setTextareaPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        setNoteStep(2);
        setTimeout(() => textareaRef.current?.focus(), 50);
      }
    };

    const onDblClick = (e) => {
      const w = worldOf(e); if (!w) return;
      const { camera, renderer } = w.three;
      const threshold = viewH() * 0.1;
      let closest = null, minDist = Infinity;
      for (const note of notesRef.current) {
        const dist = Math.hypot(w.pt.x - note.bx, w.pt.y - note.by);
        if (dist < threshold && dist < minDist) { minDist = dist; closest = note; }
      }
      if (!closest) return;
      e.stopPropagation();
      setEditingId(closest.id);
      setNoteStep(2);
      pendingRef.current = { tx: closest.tx, ty: closest.ty, bx: closest.bx, by: closest.by };
      const rect = renderer.domElement.getBoundingClientRect();
      // Dünya → ekran: kamera projeksiyonu (raycaster'ın tersi)
      const v = new THREE.Vector3(closest.bx, closest.by, 1).project(camera);
      setTextareaPos({ x: (v.x * 0.5 + 0.5) * rect.width, y: (-v.y * 0.5 + 0.5) * rect.height });
      setTimeout(() => {
        if (textareaRef.current) { textareaRef.current.value = closest.text; textareaRef.current.focus(); textareaRef.current.select(); }
      }, 50);
    };

    const onMove = (e) => {
      // Yerleştirme önizleme çizgisi
      if (noteModeRef.current && noteStepRef.current === 1 && previewRef.current.line) {
        const w = worldOf(e);
        if (w) {
          const posAttr = previewRef.current.line.geometry.getAttribute('position');
          posAttr.setXYZ(1, w.pt.x, w.pt.y, 2); posAttr.needsUpdate = true;
          previewRef.current.line.computeLineDistances();
        }
      }
      // Resize
      if (resizeRef.current) {
        const w = worldOf(e); if (!w) return;
        const { noteId, startWorldX, startFrameWidth, baseWorldW, text, header } = resizeRef.current;
        const deltaFW = (w.pt.x - startWorldX) / baseWorldW;
        let next = Math.max(CALLOUT_MIN_FRAME_WIDTH, Math.min(CALLOUT_MAX_FRAME_WIDTH, startFrameWidth + deltaFW));
        if (Math.abs(next - (resizeRef.current.lastFrameWidth || 0)) < 0.01) return;
        const group = calloutGroupsRef.current.get(noteId); if (!group) return;
        const oldSprite = group.children.find(c => c.isSprite); if (!oldSprite) return;
        const pos = oldSprite.position.clone();
        const newSprite = makeTextSprite(text || '', viewH(), header || null, next);
        newSprite.position.copy(pos); newSprite.renderOrder = 999;
        newSprite.userData = { _noteId: noteId, _frameWidth: next, _text: text, _header: header };
        group.remove(oldSprite);
        if (oldSprite.material.map) oldSprite.material.map.dispose();
        oldSprite.material.dispose();
        group.add(newSprite);
        resizeRef.current.lastFrameWidth = next;
      }
    };

    const onUp = () => {
      if (resizeRef.current && resizeRef.current.lastFrameWidth != null) {
        const { noteId, lastFrameWidth, startFrameWidth } = resizeRef.current;
        if (lastFrameWidth !== startFrameWidth) {
          setNotes(prev => prev.map(n => n.id === noteId ? { ...n, frameWidth: lastFrameWidth } : n));
          if (projectId && !String(noteId).startsWith('note_')) {
            fetch(`${API_URL}/api/projects/${projectId}/notes/${noteId}`, {
              method: 'PATCH', headers: apiHeaders(),
              body: JSON.stringify({ calloutData: { frameWidth: lastFrameWidth } }),
            }).catch(() => {});
          }
        }
      }
      resizeRef.current = null;
    };

    container.addEventListener('mousedown', onDown, true);
    container.addEventListener('click', onClick, true);
    container.addEventListener('dblclick', onDblClick, true);
    container.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      container.removeEventListener('mousedown', onDown, true);
      container.removeEventListener('click', onClick, true);
      container.removeEventListener('dblclick', onDblClick, true);
      container.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [enabled, ready, containerRef, getThree, hitTestHandle, projectId, apiHeaders]);

  // ESC ile not iptali
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (filterOpen) { setFilterOpen(false); return; }
      if (noteStepRef.current > 0) {
        setNoteStep(0); setTextareaPos(null); setEditingId(null); pendingRef.current = null;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [filterOpen]);

  // Filtre dropdown dış-tık
  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e) => { if (!e.target.closest('.jbz-filter-wrap')) setFilterOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterOpen]);

  const toggleNoteMode = useCallback(() => {
    setNoteMode(m => !m); setNoteStep(0); setTextareaPos(null); pendingRef.current = null;
  }, []);

  return {
    noteMode, setNoteMode, toggleNoteMode,
    noteStep, notes, hiddenAuthors, setHiddenAuthors, filterOpen, setFilterOpen,
    textareaPos, editingId, textareaRef,
    handleNoteSubmit, handleNoteDelete, handleNoteTextKeyDown,
    CALLOUT_CSS,
  };
}
